import React, { useEffect, useRef, useState } from "react";

import { DEMO_KEYFRAMES } from "../components/demo/animations";
import { GrowthHud } from "../components/demo/GrowthHud";
import {
  SCENES,
  N_SCENES,
  SCENE_DURATIONS_MS,
  TOTAL_DURATION_MS,
} from "../components/demo/scenes";

const FONT = "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * PublicDemo — 45s autoplay-muted-looping animated walkthrough built from
 * the real platform screenshots. See `components/demo/scenes/index.js`
 * for the scene sequence and per-scene durations.
 *
 * Behaviour:
 *   • Autoplay on mount, no controls, loops forever.
 *   • Each scene has its own duration (see SCENE_DURATIONS_MS).
 *   • Persistent Impact HUD ticks 0 → +$76.8k over the full 45s.
 *   • Bottom progress bar has one segment per scene, sized by duration.
 *   • Fires GA4 `video_play` once on mount and `video_loop` on every loop.
 */
const PublicDemo = () => {
  const [scene, setScene] = useState(0);
  const [loopId, setLoopId] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "video_play", { video_title: "aigo_45s_screenshot_walkthrough" });
    }
  }, []);

  useEffect(() => {
    // Schedule the transition to the next scene / next loop
    const dur = SCENE_DURATIONS_MS[scene] || 8000;
    timerRef.current = setTimeout(() => {
      if (scene === N_SCENES - 1) {
        // loop
        if (window.gtag) window.gtag("event", "video_loop", { video_title: "aigo_45s_screenshot_walkthrough" });
        setLoopId((l) => l + 1);
        setScene(0);
      } else {
        setScene((s) => s + 1);
      }
    }, dur);
    return () => clearTimeout(timerRef.current);
  }, [scene]);

  const SceneComponent = SCENES[scene];

  return (
    <div
      data-testid="public-demo-root"
      className="relative w-full h-full min-h-[440px] sm:min-h-[540px] overflow-hidden rounded-2xl border border-slate-200 bg-white"
      style={{ fontFamily: FONT }}
    >
      {/* Scene layer — key remounts on scene+loop so animations restart cleanly */}
      <div className="absolute inset-0" key={`${loopId}-${scene}`}>
        <SceneComponent />
      </div>

      {/* Progress bar — one segment per scene, sized by duration */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/25 z-30">
        <div className="flex h-full w-full">
          {SCENE_DURATIONS_MS.map((ms, i) => (
            <div
              key={`${loopId}-seg-${i}`}
              className="relative overflow-hidden border-r border-black/20 last:border-r-0"
              style={{ flex: ms }}
            >
              <div
                className="absolute inset-0 origin-left"
                style={{
                  background: "linear-gradient(90deg,#10b981,#34d399)",
                  transform: i < scene ? "scaleX(1)" : "scaleX(0)",
                  animation: i === scene ? `pd-progress ${ms}ms linear forwards` : "none",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <GrowthHud
        loopId={loopId}
        totalMs={TOTAL_DURATION_MS}
        sceneIndex={scene}
        sceneCount={N_SCENES}
      />

      <style>{DEMO_KEYFRAMES}</style>
    </div>
  );
};

export default PublicDemo;
